import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  InitializeState,
  InitializeStatus
} from 'common/modules/app/initialize';
import { GlossaryState } from 'common/modules/app/glossaries';
import { Glossary } from 'common/utils/glossary';
import { BoardStatus, setStatus } from 'common/modules/app/board';
import {
  UserInformation,
  updateUser,
  UpdateKeyValue
} from 'common/modules/domain/user';
import { Master } from 'common/modules/app/master';
import { Button, Column } from 'common/components';

import { AppState } from 'pages';

interface OwnProps {
  /** アイコンボタンやリストのカラムなどの種類 */
  type: 'button' | 'column';
  /** テスト用識別子 */
  'data-test': string;
}

// StoreのStateからComponentに渡すPropsへ
const useStateProps = () => {
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const masterState = useSelector<AppState, Master>(
    (state) => state.app.master
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const initState = useSelector<AppState, InitializeState>(
    (state) => state.app.initialize
  );

  return {
    master: masterState,
    user: userState,
    glossary: Glossary.instance(glState),
    isCompleteInit: initState.status === InitializeStatus.FINISH
  };
};
type SelectedState = ReturnType<typeof useStateProps>;

// StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _master = state.master;
  const _user = state.user;

  // 国マスターファイルから選択されている国がどの地域に所属するか確認
  const _selectRegion = () => {
    for (const [key, value] of Object.entries(_master.country)) {
      // 地域に所属する国リストに存在するか確認
      for (const country of value.countries) {
        if (country.code === _user.country) {
          return key;
        }
      }
    }
  };

  /** 初期化処理 */
  const onInit = () => {
    const region = _selectRegion();
    if (region) {
      dispatch(updateUser(UpdateKeyValue.KEY_REGION, region));
    }
    const _region = (region && _master.country[region]) || { countries: [] };
    return _region.countries.find((c) => c.code === _user.country);
  };

  /** 国/地域の更新イベントの処理 */
  const onChangeCountry = () => {
    const region = _master.country[_user.region] || { countries: [] };
    return region.countries.find((c) => c.code === _user.country);
  };

  /** クリック処理 */
  const onClickCountry = () => {
    const region = _selectRegion();
    if (region) {
      dispatch(updateUser(UpdateKeyValue.KEY_REGION, region));
    }
    dispatch(setStatus(BoardStatus.COUNTRY_DISP));
  };

  return {
    onClick: onClickCountry,
    onChangeCountry: onChangeCountry,
    onInit: onInit,
    ...state
  };
};

/**
 * 国/地域のボタンのContainer Component
 */
export default function ContainerComponent(
  ownProps: OwnProps & React.ComponentProps<'div'>
) {
  const _props = {
    ...useMargeProps(useStateProps())
  };
  // 表示用のLocalState。地域がクリックされても、表示は変更しないために保持する。
  const [countryGL, setCountryGL] = React.useState<string>('');

  // 初期化処理イベント
  React.useEffect(() => {
    _props.isCompleteInit && _props.onInit();
  }, [_props.isCompleteInit]);

  // 国/地域の更新イベント
  React.useEffect(() => {
    const newCountry = _props.onChangeCountry();
    newCountry && setCountryGL(newCountry.glossary);
  }, [_props.isCompleteInit, _props.user]);

  const _button = (
    <Button type="countryButton" data-test={ownProps['data-test']} {..._props}>
      {_props.glossary.glx(countryGL)}
    </Button>
  );
  const _column = (
    <Column
      type="item"
      subType="country full"
      data-test={ownProps['data-test']}
      {..._props}
    >
      <div className="countryName">{_props.glossary.glx(countryGL)}</div>
    </Column>
  );
  return ownProps.type === 'button' ? _button : _column;
}
